import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    Alert,
    Snackbar,
    Stack,
    IconButton,
    Chip,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Settings as SettingsIcon,
    AccountBalance as AccountIcon,
    MenuBook as JournalIcon,
    Percent as TvaIcon,
    Schema as SchemaIcon,
    CloudDownload as ExportIcon,
    People as ClientIcon,
    AutoFixHigh as AutoCodeIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    FileDownload as DownloadIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients } from '../../data/slices/clientStore';
import PageHeader from '../../components/layout/PageHeader';
import {
    getSageSettings,
    saveSageSettings,
    getClientSageAuxCodeMap,
    saveClientSageAuxCodeMap,
    getClientFullName,
    getClientSageAuxCode,
    INITIAL_CONFIRMED_TIERS_MAP,
    DEFAULT_COMPTES_GENERAUX,
    DEFAULT_JOURNAUX,
    DEFAULT_CODES_TVA,
    DEFAULT_EXPORT_SETTINGS,
    buildSageExportFileContent,
    downloadSageFile
} from '../../utils/sageAccountingEngine';
import { COLORS } from '../../settings/themeSettings';

const ParametrageComptablePage = () => {
    const dispatch = useDispatch();
    const { clients } = useSelector((state) => state.clients);

    const [activeTab, setActiveTab] = useState(0);

    // Données de configuration
    const [comptes, setComptes] = useState(DEFAULT_COMPTES_GENERAUX);
    const [journaux, setJournaux] = useState(DEFAULT_JOURNAUX);
    const [codesTva, setCodesTva] = useState(DEFAULT_CODES_TVA);
    const [exportSettings, setExportSettings] = useState(DEFAULT_EXPORT_SETTINGS);

    // Table de correspondance Client SAGE
    const [clientTiersMap, setClientTiersMap] = useState({});
    const [clientSearchQuery, setClientSearchQuery] = useState('');

    // Notification toast
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Modales de création / édition
    const [compteDialog, setCompteDialog] = useState({ open: false, isEdit: false, data: {} });
    const [journalDialog, setJournalDialog] = useState({ open: false, isEdit: false, data: {} });
    const [tvaDialog, setTvaDialog] = useState({ open: false, isEdit: false, data: {} });
    const [clientTiersDialog, setClientTiersDialog] = useState({ open: false, client: null, codeTiers: '' });
    const [schemaSubTab, setSchemaSubTab] = useState(0);

    useEffect(() => {
        dispatch(fetchClients());
        const loaded = getSageSettings();
        setComptes(loaded.comptes);
        setJournaux(loaded.journaux);
        setCodesTva(loaded.codesTva);
        setExportSettings(loaded.exportSettings);
        setClientTiersMap(getClientSageAuxCodeMap());
    }, [dispatch]);

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const handleSaveAll = () => {
        saveSageSettings({ comptes, journaux, codesTva, exportSettings });
        saveClientSageAuxCodeMap(clientTiersMap);
        showToast('Paramétrage comptable enregistré avec succès.');
    };

    // ─── GESTION DE LA TABLE DE CORRESPONDANCE CLIENTS ↔ SAGE ────────────────

    const clientListEnriched = useMemo(() => {
        return (clients || []).map(c => {
            const nomComplet = getClientFullName(c);
            const nomBase = (c.nom || c.nomComplet || c.libelle || nomComplet).trim();
            const clientId = String(c.id);
            const currentCode = clientTiersMap[clientId] || 
                                clientTiersMap[nomComplet.trim().toLowerCase()] || 
                                clientTiersMap[nomBase.trim().toLowerCase()] || 
                                getClientSageAuxCode(c, nomComplet);
            const isValide = currentCode && currentCode !== '4111Àcréer' && currentCode.startsWith('4111');
            return {
                ...c,
                nomAffiche: nomComplet,
                codeTiersSage: currentCode,
                isValide
            };
        });
    }, [clients, clientTiersMap]);

    const filteredClientList = useMemo(() => {
        if (!clientSearchQuery) return clientListEnriched;
        const q = clientSearchQuery.toLowerCase();
        return clientListEnriched.filter(c =>
            (c.nomAffiche || '').toLowerCase().includes(q) ||
            (c.codeTiersSage || '').toLowerCase().includes(q)
        );
    }, [clientListEnriched, clientSearchQuery]);

    const handleAutoGenerateAllCodes = () => {
        const updatedMap = { ...INITIAL_CONFIRMED_TIERS_MAP };
        let count = 0;
        (clients || []).forEach(c => {
            const nomComplet = getClientFullName(c);
            const newCode = getClientSageAuxCode(c, nomComplet);
            updatedMap[String(c.id)] = newCode;
            if (nomComplet) updatedMap[nomComplet.trim().toLowerCase()] = newCode;
            if (c.nom) updatedMap[c.nom.trim().toLowerCase()] = newCode;
            count++;
        });
        setClientTiersMap(updatedMap);
        saveClientSageAuxCodeMap(updatedMap);
        showToast(`${count} compte(s) client(s) régénéré(s) au format nominal officiel SAGE.`);
    };

    const handleSaveSingleClientTiers = () => {
        if (!clientTiersDialog.client || !clientTiersDialog.codeTiers) return;
        const code = clientTiersDialog.codeTiers.trim().toUpperCase();
        const finalCode = code.startsWith('4111') ? code : `4111${code}`;
        const clientObj = clientTiersDialog.client;
        const nomComplet = clientObj.nomAffiche || getClientFullName(clientObj);

        const updatedMap = {
            ...clientTiersMap,
            [String(clientObj.id)]: finalCode,
            [nomComplet.trim().toLowerCase()]: finalCode
        };
        if (clientObj.nom) {
            updatedMap[clientObj.nom.trim().toLowerCase()] = finalCode;
        }
        setClientTiersMap(updatedMap);
        saveClientSageAuxCodeMap(updatedMap);
        setClientTiersDialog({ open: false, client: null, codeTiers: '' });
        showToast(`Code SAGE mis à jour : ${finalCode}`);
    };

    // ─── GESTION DES COMPTES GÉNÉRAUX ────────────────────────────────────────

    const handleOpenCompteModal = (compte = null) => {
        if (compte) {
            setCompteDialog({ open: true, isEdit: true, data: { ...compte } });
        } else {
            setCompteDialog({
                open: true,
                isEdit: false,
                data: { id: Date.now().toString(), numero: '', intitule: '', nature: 'Aucune', sensNormal: 'Débit', codeTva: '' }
            });
        }
    };

    const handleSaveCompte = () => {
        if (!compteDialog.data.numero || !compteDialog.data.intitule) {
            showToast('Le numéro et l\'intitulé de compte sont obligatoires.', 'warning');
            return;
        }
        if (compteDialog.data.nature === 'Produits' && !compteDialog.data.codeTva) {
            showToast('Le code TVA applicable est obligatoire lorsque la nature est "Produits".', 'warning');
            return;
        }

        let updated;
        if (compteDialog.isEdit) {
            updated = comptes.map(c => c.id === compteDialog.data.id ? compteDialog.data : c);
        } else {
            updated = [...comptes, compteDialog.data];
        }
        setComptes(updated);
        saveSageSettings({ comptes: updated });
        setCompteDialog({ open: false, isEdit: false, data: {} });
        showToast('Compte général enregistré.');
    };

    const handleDeleteCompte = (id) => {
        const updated = comptes.filter(c => c.id !== id);
        setComptes(updated);
        saveSageSettings({ comptes: updated });
        showToast('Compte supprimé.');
    };

    const handleResetToAftDefaults = () => {
        setComptes(DEFAULT_COMPTES_GENERAUX);
        setJournaux(DEFAULT_JOURNAUX);
        setCodesTva(DEFAULT_CODES_TVA);
        setExportSettings(DEFAULT_EXPORT_SETTINGS);
        saveSageSettings({
            comptes: DEFAULT_COMPTES_GENERAUX,
            journaux: DEFAULT_JOURNAUX,
            codesTva: DEFAULT_CODES_TVA,
            exportSettings: DEFAULT_EXPORT_SETTINGS
        });
        showToast('Paramètres réinitialisés avec le plan comptable officiel SAGE AFT.');
    };

    // ─── GESTION DES JOURNAUX ────────────────────────────────────────────────

    const handleResetJournauxToDefaults = () => {
        setJournaux(DEFAULT_JOURNAUX);
        saveSageSettings({ journaux: DEFAULT_JOURNAUX });
        showToast('Journaux comptables réinitialisés selon la configuration officielle SAGE (FAC, VTES).');
    };

    const handleOpenJournalModal = (journal = null) => {
        if (journal) {
            setJournalDialog({ open: true, isEdit: true, data: { ...journal } });
        } else {
            setJournalDialog({
                open: true,
                isEdit: false,
                data: { id: Date.now().toString(), code: '', intitule: '', type: 'Général', numerotation: 'Continue', description: '' }
            });
        }
    };

    const handleSaveJournal = () => {
        if (!journalDialog.data.code || !journalDialog.data.intitule) {
            showToast('Le code et l\'intitulé du journal sont obligatoires.', 'warning');
            return;
        }
        const cleanedData = {
            ...journalDialog.data,
            code: (journalDialog.data.code || '').trim().toUpperCase(),
            intitule: (journalDialog.data.intitule || '').trim(),
            description: (journalDialog.data.description || '').trim()
        };
        let updated;
        if (journalDialog.isEdit) {
            updated = journaux.map(j => j.id === cleanedData.id ? cleanedData : j);
        } else {
            updated = [...journaux, cleanedData];
        }
        setJournaux(updated);
        saveSageSettings({ journaux: updated });
        setJournalDialog({ open: false, isEdit: false, data: {} });
        showToast('Journal comptable enregistré.');
    };

    const handleDeleteJournal = (id) => {
        const updated = journaux.filter(j => j.id !== id);
        setJournaux(updated);
        saveSageSettings({ journaux: updated });
        showToast('Journal supprimé.');
    };

    // ─── GESTION DES CODES TVA ───────────────────────────────────────────────

    const handleResetTvaToDefaults = () => {
        setCodesTva(DEFAULT_CODES_TVA);
        saveSageSettings({ codesTva: DEFAULT_CODES_TVA });
        showToast('Codes TVA réinitialisés selon la configuration officielle SAGE (TVA1 18% / TVA0 0%).');
    };

    const handleOpenTvaModal = (tva = null) => {
        if (tva) {
            setTvaDialog({ open: true, isEdit: true, data: { ...tva } });
        } else {
            setTvaDialog({
                open: true,
                isEdit: false,
                data: { id: Date.now().toString(), code: '', intitule: '', sens: 'Facturée', taux: 18, compteTaxe: '443200', description: '' }
            });
        }
    };

    const handleSaveTva = () => {
        if (!tvaDialog.data.code || !tvaDialog.data.intitule) {
            showToast('Le code taxe et l\'intitulé sont obligatoires.', 'warning');
            return;
        }
        const cleanedData = {
            ...tvaDialog.data,
            code: (tvaDialog.data.code || '').trim().toUpperCase(),
            intitule: (tvaDialog.data.intitule || '').trim(),
            description: (tvaDialog.data.description || '').trim(),
            compteTaxe: (tvaDialog.data.compteTaxe || '').trim(),
            taux: Number(tvaDialog.data.taux || 0)
        };
        let updated;
        if (tvaDialog.isEdit) {
            updated = codesTva.map(t => t.id === cleanedData.id ? cleanedData : t);
        } else {
            updated = [...codesTva, cleanedData];
        }
        setCodesTva(updated);
        saveSageSettings({ codesTva: updated });
        setTvaDialog({ open: false, isEdit: false, data: {} });
        showToast('Code TVA enregistré.');
    };

    const handleDeleteTva = (id) => {
        const updated = codesTva.filter(t => t.id !== id);
        setCodesTva(updated);
        saveSageSettings({ codesTva: updated });
        showToast('Code TVA supprimé.');
    };

    // ─── GESTION DES PARAMÈTRES D'EXPORT SAGE ────────────────────────────────

    const handleResetExportToDefaults = () => {
        setExportSettings(DEFAULT_EXPORT_SETTINGS);
        saveSageSettings({ exportSettings: DEFAULT_EXPORT_SETTINGS });
        showToast('Paramètres d\'export SAGE réinitialisés (AFRICA FOR TOURISM / C:\\Users\\HP\\Desktop).');
    };

    const handleDownloadSampleFile = () => {
        const sampleEcritures = [
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '411100',
                compteTiers: '4111FABRE',
                libelle: 'FACT. FT-EXEMPLE-01 - PIERRE FABRE',
                dateEcheance: dayjs().add(30, 'day').format('DD/MM/YYYY'),
                debit: 275800,
                credit: 0
            },
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '471120',
                compteTiers: '',
                libelle: 'Débours s/dossiers circuits',
                dateEcheance: '',
                debit: 0,
                credit: 200000
            },
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '706110',
                compteTiers: '',
                libelle: 'FACT. FT-EXEMPLE-01 - PIERRE FABRE',
                dateEcheance: '',
                debit: 0,
                credit: 64237
            },
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '443200',
                compteTiers: '',
                libelle: 'TVA FACT. FT-EXEMPLE-01',
                dateEcheance: '',
                debit: 0,
                credit: 11563
            }
        ];
        const content = buildSageExportFileContent(sampleEcritures, exportSettings);
        const filename = `${exportSettings.prefixeFichier || 'TM_BASCULE_'}${dayjs().format('YYYYMMDD')}_TEST.txt`;
        downloadSageFile(content, filename);
        showToast(`Fichier test généré et téléchargé : ${filename}`);
    };

    const handleDownloadSampleCsvFile = () => {
        const sampleEcritures = [
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '411100',
                compteTiers: '4111FABRE',
                libelle: 'FACT. FT-EXEMPLE-01 - PIERRE FABRE',
                dateEcheance: dayjs().add(30, 'day').format('DD/MM/YYYY'),
                debit: 275800,
                credit: 0
            },
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '471120',
                compteTiers: '',
                libelle: 'Débours s/dossiers circuits',
                dateEcheance: '',
                debit: 0,
                credit: 200000
            },
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '706110',
                compteTiers: '',
                libelle: 'FACT. FT-EXEMPLE-01 - PIERRE FABRE',
                dateEcheance: '',
                debit: 0,
                credit: 64237
            },
            {
                jour: dayjs().format('DD'),
                piece: '',
                numeroFacture: 'FT-EXEMPLE-01',
                numeroDossier: 'CIR-2026-001',
                compteGeneral: '443200',
                compteTiers: '',
                libelle: 'TVA FACT. FT-EXEMPLE-01',
                dateEcheance: '',
                debit: 0,
                credit: 11563
            }
        ];
        const content = buildSageExportFileContent(sampleEcritures, exportSettings);
        const filename = `${exportSettings.prefixeFichier || 'TM_BASCULE_'}${dayjs().format('YYYYMMDD')}_TEST.csv`;
        downloadSageFile(content, filename);
        showToast(`Fichier CSV test généré et téléchargé : ${filename}`);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader
                title="Paramétrage comptable SAGE"
                subtitle="Configuration du plan de comptes, des tiers clients, des journaux et des exports"
                icon={SettingsIcon}
                breadcrumbs={[
                    { label: 'Interface SAGE', path: '/sage/parametres' },
                    { label: 'Paramétrage', path: '/sage/parametres' }
                ]}
            />

            <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white', overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 }
                    }}
                >
                    <Tab icon={<AccountIcon fontSize="small" />} iconPosition="start" label="Comptes généraux" />
                    <Tab icon={<ClientIcon fontSize="small" />} iconPosition="start" label="Comptes de tiers clients" />
                    <Tab icon={<JournalIcon fontSize="small" />} iconPosition="start" label="Journaux comptables" />
                    <Tab icon={<TvaIcon fontSize="small" />} iconPosition="start" label="Codes TVA" />
                    <Tab icon={<SchemaIcon fontSize="small" />} iconPosition="start" label="Schémas comptables" />
                    <Tab icon={<ExportIcon fontSize="small" />} iconPosition="start" label="Paramètres d'export SAGE" />
                </Tabs>

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 1 : COMPTES GÉNÉRAUX                                   */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 0 && (
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                                Plan de comptes généraux (SYSCOHADA & SAGE AFT)
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={handleResetToAftDefaults}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Rétablir plan officiel SAGE AFT
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={() => handleOpenCompteModal()}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Créer un compte général
                                </Button>
                            </Stack>
                        </Stack>

                        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#0f172a' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>N° Compte</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Intitulé</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nature</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sens Normal</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code TVA Applicable</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {comptes.map((c) => (
                                        <TableRow key={c.id} hover>
                                            <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>{c.numero}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{c.intitule}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={c.nature} color={c.nature === 'Produits' ? 'success' : (c.nature === 'Client' ? 'primary' : 'default')} />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{c.sensNormal}</TableCell>
                                            <TableCell>{c.codeTva || '—'}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                <IconButton size="small" onClick={() => handleOpenCompteModal(c)} color="primary">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteCompte(c.id)} color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 2 : COMPTES DE TIERS CLIENTS                           */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                                    Table de correspondance Client TravelManager ↔ Compte Auxiliaire SAGE
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Chaque client dispose d'un compte auxiliaire (ex. 4111FABRE pour PIERRE FABRE) rattaché au compte général 411100.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AutoCodeIcon />}
                                    size="small"
                                    onClick={handleAutoGenerateAllCodes}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Générer automatiquement
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    size="small"
                                    onClick={handleSaveAll}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Enregistrer la table
                                </Button>
                            </Stack>
                        </Stack>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Rechercher un client par nom ou code SAGE..."
                            value={clientSearchQuery}
                            onChange={(e) => setClientSearchQuery(e.target.value)}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                )
                            }}
                        />

                        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '55vh' }}>
                            <Table size="small" stickyHeader>
                                <TableHead sx={{ bgcolor: '#0f172a' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#0f172a' }}>Client / Raison Sociale</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#0f172a' }}>Compte Général</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#0f172a' }}>Compte Auxiliaire SAGE</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#0f172a', textAlign: 'center' }}>Statut SAGE</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', bgcolor: '#0f172a', textAlign: 'right' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredClientList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                                Aucun client trouvé.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredClientList.map((c) => (
                                            <TableRow key={c.id} hover>
                                                <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{c.nomAffiche}</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>411100</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: c.isValide ? '#2563eb' : '#dc2626' }}>
                                                    {c.codeTiersSage}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    {c.isValide ? (
                                                        <Chip size="small" icon={<CheckCircleIcon />} label="Validé" color="success" />
                                                    ) : (
                                                        <Chip size="small" icon={<WarningIcon />} label="4111Àcréer" color="error" />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>
                                                    <Button
                                                        size="small"
                                                        startIcon={<EditIcon />}
                                                        onClick={() => setClientTiersDialog({ open: true, client: c, codeTiers: c.codeTiersSage })}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        Modifier
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 3 : JOURNAUX COMPTABLES                                */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} flexWrap="wrap" gap={1.5}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                                    Registre des Journaux Comptables
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Définition des registres de saisie comptable pour la facturation et les opérations SAGE
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    size="small"
                                    onClick={handleResetJournauxToDefaults}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Rétablir journaux officiels
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={() => handleOpenJournalModal()}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Créer un journal
                                </Button>
                            </Stack>
                        </Stack>

                        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#0f172a' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Intitulé</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type de journal</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type d'Opérations couvertes</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Numérotation</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {journaux.map((j) => (
                                        <TableRow key={j.id} hover>
                                            <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>{j.code}</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{j.intitule}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={j.type || 'Général'}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, fontSize: '0.75rem', borderColor: '#cbd5e1' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                                {j.description || (j.code === 'FAC' ? 'Facturation clients (Tourisme et Billetterie) — Journal unique' : j.code === 'VTES' ? 'Opérations de ventes — Reconnaissance C.A. (marges), Débours et TVA' : j.type)}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    size="small"
                                                    label={j.numerotation === 'Mensuelle' ? 'Mensuelle (1-N/mois)' : 'Continue (1-N)'}
                                                    color={j.numerotation === 'Mensuelle' ? 'secondary' : 'default'}
                                                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                <IconButton size="small" onClick={() => handleOpenJournalModal(j)} color="primary" title="Modifier le journal">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteJournal(j.id)} color="error" title="Supprimer le journal">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px dashed #cbd5e1' }}>
                            <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1.5 }}>
                                <strong>Règle de gestion :</strong> Le transfert des factures vers SAGE est exécuté via le journal unique de facturation <strong>FAC</strong> et le journal des ventes <strong>VTES</strong>. Les journaux de trésorerie (Banque, Caisse) et opérations diverses peuvent être paramétrés selon les besoins.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 4 : CODES TVA                                          */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 3 && (
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} flexWrap="wrap" gap={1.5}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                                    Configuration des Taux et Codes TVA
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Définition des taux applicables et des comptes de taxe rattachés aux produits
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    size="small"
                                    onClick={handleResetTvaToDefaults}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Rétablir codes TVA officiels
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={() => handleOpenTvaModal()}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Créer un code TVA
                                </Button>
                            </Stack>
                        </Stack>

                        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#0f172a' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code Taxe</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Intitulé</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Application / Description</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Sens</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Taux</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte de Taxe SAGE</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {codesTva.map((t) => (
                                        <TableRow key={t.id} hover>
                                            <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>{t.code}</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{t.intitule}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                                {t.description || (t.code === 'TVA1' ? 'TVA taux normal — prestations touristiques soumises' : t.code === 'TVA0' ? 'Exonéré — billetterie aérienne (hors taxes)' : '—')}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                <Chip
                                                    size="small"
                                                    label={t.sens || 'Facturée'}
                                                    variant="outlined"
                                                    color={t.sens === 'Facturée' ? 'primary' : 'default'}
                                                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', fontWeight: 800, color: t.taux > 0 ? '#0f172a' : 'text.secondary' }}>
                                                {t.taux} %
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: t.compteTaxe ? '#2563eb' : 'text.secondary' }}>
                                                {t.compteTaxe ? `${t.compteTaxe} (ETAT - TVA FACTUREE)` : '— (sans TVA / exonéré)'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>
                                                <IconButton size="small" onClick={() => handleOpenTvaModal(t)} color="primary" title="Modifier le code TVA">
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteTva(t.id)} color="error" title="Supprimer le code TVA">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px dashed #cbd5e1' }}>
                            <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1.5 }}>
                                <strong>Règle de gestion — Lien entre comptes de produits et codes TVA :</strong>
                                <br />• <strong>Comptes taxables :</strong> liés à un code TVA avec taux (ex. <strong>TVA1 à 18%</strong>). La TVA facturée est cumulée automatiquement dans le compte <strong>443200</strong> lors de la bascule vers SAGE.
                                <br />• <strong>Comptes exonérés :</strong> liés à un code sans taux (ex. <strong>TVA0 à 0%</strong>). Aucun calcul de TVA ni compte de taxe mouvementé.
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 5 : SCHÉMAS COMPTABLES                                 */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 4 && (
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ mb: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                                Modèles de Schémas d'Écritures Comptables SAGE
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Les schémas d'écritures définissent le modèle comptable généré automatiquement lors de chaque bascule vers SAGE Comptabilité.
                            </Typography>
                        </Box>

                        {/* Sous-navigation des schémas */}
                        <Tabs
                            value={schemaSubTab}
                            onChange={(_, val) => setSchemaSubTab(val)}
                            sx={{
                                minHeight: '38px',
                                mb: 2.5,
                                borderBottom: '1px solid #e2e8f0',
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    minHeight: '38px',
                                    py: 0.5
                                }
                            }}
                        >
                            <Tab label="1. Schéma Unique (Journal FAC)" />
                            <Tab label="2. Détail par Activité (Tourisme & Billetterie)" />
                            <Tab label="3. Articulation Journalière (Journaux FAC & VTES)" />
                        </Tabs>

                        {/* ═════════════════════════════════════════════════════════════ */}
                        {/* SOUS-ONGLET 1 : SCHÉMA UNIQUE (JOURNAL FAC)                   */}
                        {/* ═════════════════════════════════════════════════════════════ */}
                        {schemaSubTab === 0 && (
                            <Box>
                                <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 0.5 }}>
                                        Principe du Schéma Unique de Facturation (Journal FAC)
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                        Un seul et unique journal de facturation (<strong>FAC</strong>) regroupe l'ensemble des activités de l'agence (Tourisme et Billetterie).
                                        Lors de chaque bascule comptable, le système lit les paramètres du dossier et génère automatiquement les 4 lignes d'écriture normalisées selon la grille de saisie officielle SAGE Comptabilité 100.
                                    </Typography>
                                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#e0f2fe', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                                        <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, display: 'block' }}>
                                            RÈGLE FONDAMENTALE DU CHIFFRE D'AFFAIRES (C.A.) :
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#0c4a6e', display: 'block' }}>
                                            Le Chiffre d'Affaires de l'agence correspond <strong>exclusivement à la marge nette dégagée</strong> (Prix de vente − Débours/Achats) et non au montant total facturé. Les débours sont comptabilisés en comptes <strong>471xxx</strong> pour équilibrer parfaitement les écritures au centime près.
                                        </Typography>
                                    </Box>
                                </Paper>

                                {/* MODÈLE GRILLE DE SAISIE SAGE 100 */}
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 1 }}>
                                    Modèle Officiel de Présentation des Écritures dans SAGE (Journal FAC)
                                </Typography>
                                <TableContainer sx={{ border: '1px solid #0f172a', borderRadius: '8px', mb: 3 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#0f172a' }}>
                                            <TableRow>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Jour</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>N° Pièce</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>N° Facture</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Référence (Dossier/PNR)</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Compte Général</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Compte Tiers</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Libellé Écriture</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Date Échéance</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'right' }}>Débit (CFA)</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'right' }}>Crédit (CFA)</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>30</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>néant</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>FT120626-0122</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>ATD/CIR/018/26</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>411100</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111FABRE</TableCell>
                                                <TableCell>FACT. FT120626-0122 - PIERRE FABRE</TableCell>
                                                <TableCell sx={{ color: '#0369a1', fontWeight: 600 }}>30/07/2026 (Auto)</TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>275 800</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>30</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>néant</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>FT120626-0122</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>ATD/CIR/018/26</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>471120</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell>Débours s/dossiers circuits</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>200 000</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>30</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>néant</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>FT120626-0122</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>ATD/CIR/018/26</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>706110</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell>FACT. FT120626-0122 - PIERRE FABRE</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>64 237</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>30</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>néant</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>FT120626-0122</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>ATD/CIR/018/26</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>443200</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell>TVA FACT. FT120626-0122</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>11 563</TableCell>
                                            </TableRow>
                                            <TableRow sx={{ bgcolor: '#f1f5f9', borderTop: '2px solid #0f172a' }}>
                                                <TableCell colSpan={8} sx={{ fontWeight: 900, textAlign: 'right', pr: 2 }}>
                                                    TOTAUX ÉCRITURE (ÉQUILIBRE DÉBIT = CRÉDIT) :
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>275 800</TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>275 800</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* DÉTAIL DES 4 LIGNES D'ÉCRITURE */}
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 1 }}>
                                    Spécification des 4 Lignes d'Écritures Normalisées
                                </Typography>
                                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', mb: 3 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#0f172a' }}>
                                            <TableRow>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>N° Ligne</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Général</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Tiers</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Libellé Automatique</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Sens</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Montant & Formule</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Conditions d'Application</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>1</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>411100 (Collectif clients) ou 411300 (Comptant)</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111[CODE] ou 4111DIVERS</TableCell>
                                                <TableCell>FACT. [N° Facture] — [Nom Client]</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Débit" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Total TTC facturé au client</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>Systématique pour toute facture émise</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>2</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>4711xx (Comptes de débours)</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell>Débours s/achats billets ou s/dossiers</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Total Débours / Coût d'achat</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>Généré si le montant des débours &gt; 0</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>3</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>7061xx (Tourisme) ou 701100 (Billetterie)</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell>FACT. [N° Facture] — [Nom Client]</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Marge nette HT (= Vente − Débours − TVA)</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>Chiffre d'affaires réel de l'agence</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 'bold' }}>4</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>443200 (ETAT - TVA FACTUREE)</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                <TableCell>TVA FACT. [N° Facture]</TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>TVA 18% calculée sur la marge</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>Uniquement si prestation soumise à TVA1</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* GESTION DES AVOIRS & ANNULATIONS */}
                                <Paper elevation={0} sx={{ p: 2, bgcolor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px' }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#92400e', mb: 0.5 }}>
                                        Traitement Comptable des Avoirs & Annulations de Factures
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#78350f', fontSize: '0.85rem' }}>
                                        Les avoirs et annulations de facture suivent exactement le même schéma comptable avec les <strong>sens Débit et Crédit inversés</strong> :
                                        le compte client <strong>411100</strong> est crédité du montant TTC de l'avoir, tandis que les comptes de débours (<strong>471xxx</strong>), de produit (<strong>70xxxx</strong>) et de TVA (<strong>443200</strong>) sont débités en contrepartie.
                                    </Typography>
                                </Paper>
                            </Box>
                        )}

                        {/* ═════════════════════════════════════════════════════════════ */}
                        {/* SOUS-ONGLET 2 : DÉTAIL PAR ACTIVITÉ (TOURISME & BILLETTERIE)  */}
                        {/* ═════════════════════════════════════════════════════════════ */}
                        {schemaSubTab === 1 && (
                            <Box>
                                {/* SCHÉMA A : TOURISME */}
                                <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: 'white' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                            Schéma A — Activité Tourisme (Circuits, Séjours, Excursions, Transferts)
                                        </Typography>
                                        <Chip size="small" label="Soumis à TVA (TVA1 18% sur Marge)" color="primary" sx={{ fontWeight: 700 }} />
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#475569', mb: 2, fontSize: '0.85rem' }}>
                                        • <strong>Régime fiscal :</strong> Les prestations touristiques sont soumises à la TVA au taux normal de 18% assise sur la marge bénéficiaire dégagée par l'agence.<br />
                                        • <strong>Formules de calcul :</strong> <code>Marge TTC = Total TTC Facturé − Débours</code> | <code>Marge HT (C.A.) = Marge TTC / 1.18</code> | <code>TVA (18%) = Marge HT × 0.18</code>.<br />
                                        • <strong>Comptes associés :</strong> Débours <strong>471100</strong> (Séjours), <strong>471120</strong> (Circuits/Excursions), <strong>471130</strong> (Transferts/Autres) | Produits <strong>706110</strong> (Circuits), <strong>706120</strong> (Excursions) | Taxe <strong>443200</strong>.
                                    </Typography>

                                    <TableContainer sx={{ border: '1px solid #cbd5e1', borderRadius: '6px', mb: 2 }}>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#1e293b' }}>
                                                <TableRow>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Réf.</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Général</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Tiers</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Libellé Généré</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Sens</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Exemple Chiffré (CFA)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L1</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>411100 (Collectif clients)</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111FABRE (Pierre Fabre)</TableCell>
                                                    <TableCell>FACT. FT120626-0122 - PIERRE FABRE</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Débit" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 800 }}>275 800</TableCell>
                                                </TableRow>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L2</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>471120 (DEBOURS CIRCUITS/EXCURSIONS)</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                    <TableCell>Débours s/dossiers circuits</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>200 000</TableCell>
                                                </TableRow>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L3</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>706110 (VENTES CIRCUITS)</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                    <TableCell>FACT. FT120626-0122 - PIERRE FABRE</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>64 237</TableCell>
                                                </TableRow>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L4</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>443200 (ETAT - TVA FACTUREE)</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                    <TableCell>TVA FACT. FT120626-0122</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>11 563</TableCell>
                                                </TableRow>
                                                <TableRow sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #1e293b' }}>
                                                    <TableCell colSpan={4} sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total Dossier Circuit Tourisme :</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#166534' }}>ÉQUILIBRÉ</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>Débit = Crédit = 275 800</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        * Note de clôture : Pour équilibrer le journal, les débours vers les prestataires (hôtels, guides, transports) sont crédités en compte 471xxx à la date d'arrêté périodique de clôture comptable (ex. 30/06/2026).
                                    </Typography>
                                </Paper>

                                {/* SCHÉMA B : BILLETTERIE */}
                                <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: 'white' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                            Schéma B — Activité Billetterie (Billets d'Avion Internationaux & Régionaux)
                                        </Typography>
                                        <Chip size="small" label="Exonéré de TVA (TVA0 0%)" color="success" sx={{ fontWeight: 700 }} />
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#475569', mb: 2, fontSize: '0.85rem' }}>
                                        • <strong>Régime fiscal :</strong> Les billets d'avion sont exonérés de TVA (code TVA0). La facture ne comporte aucune TVA facturée (TTC = HT).<br />
                                        • <strong>Formule C.A. :</strong> <code>Marge nette = Prix de vente billet facturé au client − Prix d'achat compagnie aérienne (BSP/IATA)</code>.<br />
                                        • <strong>Comptes associés :</strong> Compte de débours <strong>471110</strong> (DEBOURS BILLETS D'AVION) et Compte de produit <strong>701100</strong> (VENTES BILLETS D'AVION).
                                    </Typography>

                                    <TableContainer sx={{ border: '1px solid #cbd5e1', borderRadius: '6px', mb: 2 }}>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#1e293b' }}>
                                                <TableRow>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Réf.</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Général</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Tiers</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Libellé Généré</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Sens</TableCell>
                                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Exemple Chiffré (CFA)</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L1</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>411100 (Collectif clients)</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111ASPT (Client ASPT)</TableCell>
                                                    <TableCell>FACT. FT120626-0145 - ASPT</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Débit" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 800 }}>550 000</TableCell>
                                                </TableRow>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L2</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>471110 (DEBOURS BILLETS D'AVION)</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                    <TableCell>Débours : achats billets d'avion</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>500 000</TableCell>
                                                </TableRow>
                                                <TableRow hover>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>L3</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>701100 (VENTES BILLETS D'AVION)</TableCell>
                                                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                    <TableCell>FACT. FT120626-0145 - ASPT</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}><Chip size="small" label="Crédit" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>50 000</TableCell>
                                                </TableRow>
                                                <TableRow sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #1e293b' }}>
                                                    <TableCell colSpan={4} sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total Dossier Billetterie Aérienne :</TableCell>
                                                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#166534' }}>ÉQUILIBRÉ</TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>Débit = Crédit = 550 000</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>

                                {/* TABLEAU CROISÉ COMPARATIF PAR NATURE DE PRESTATION */}
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 1 }}>
                                    Matrice Complète de Correspondance par Nature de Prestation
                                </Typography>
                                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#0f172a' }}>
                                            <TableRow>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nature de Prestation</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Produit (Marge C.A.)</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Débours (Achats)</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code TVA</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Taux TVA</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Compte Taxe SAGE</TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Journal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 700 }}>Circuits Touristiques</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>706110 (VENTES CIRCUITS)</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>471120 (DEBOURS CIRCUITS/EXCURSIONS)</TableCell>
                                                <TableCell><Chip size="small" label="TVA1" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>18 %</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>443200 (ETAT - TVA FACTUREE)</TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>FAC / VTES</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 700 }}>Excursions</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>706120 (VENTES EXCURSIONS)</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>471120 (DEBOURS CIRCUITS/EXCURSIONS)</TableCell>
                                                <TableCell><Chip size="small" label="TVA1" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>18 %</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>443200 (ETAT - TVA FACTUREE)</TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>FAC / VTES</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 700 }}>Séjours & Hébergements</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>706110 (VENTES SEJOURS)</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>471100 (DEBOURS SEJOURS)</TableCell>
                                                <TableCell><Chip size="small" label="TVA1" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>18 %</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>443200 (ETAT - TVA FACTUREE)</TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>FAC / VTES</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 700 }}>Transferts & Transports</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>706110 (VENTES TRANSFERTS)</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>471130 (DEBOURS TRANSFERTS/AUTRES)</TableCell>
                                                <TableCell><Chip size="small" label="TVA1" color="primary" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>18 %</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>443200 (ETAT - TVA FACTUREE)</TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>FAC / VTES</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell sx={{ fontWeight: 700 }}>Billetterie Aérienne</TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: COLORS.primary }}>701100 (VENTES BILLETS D'AVION)</TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>471110 (DEBOURS BILLETS D'AVION)</TableCell>
                                                <TableCell><Chip size="small" label="TVA0" color="success" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>0 %</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>— (Exonéré)</TableCell>
                                                <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>FAC / VTES</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {/* ═════════════════════════════════════════════════════════════ */}
                        {/* SOUS-ONGLET 3 : ARTICULATION JOURNALIÈRE (JOURNAUX FAC & VTES)*/}
                        {/* ═════════════════════════════════════════════════════════════ */}
                        {schemaSubTab === 2 && (
                            <Box>
                                <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 0.5 }}>
                                        Architecture d'Articulation des Journaux FAC & VTES avec Compte Pivot 411900
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.85rem', lineHeight: 1.6 }}>
                                        Dans cette organisation comptable à deux journaux :
                                        <br />1. Les créances clients sont constatées au fur et à mesure dans le journal de facturation (<strong>FAC</strong>) contre le compte pivot centralisateur <strong>411900 (CLIENT FACTURATION)</strong> crédité.
                                        <br />2. À la clôture périodique, le journal des ventes (<strong>VTES</strong>) solde le compte pivot <strong>411900</strong> au débit et ventile les débours réels (<strong>471xxx</strong>), le C.A. marge (<strong>70xxxx</strong>) et la TVA collectée (<strong>443200</strong>).
                                    </Typography>
                                    <Box sx={{ mt: 1.5, p: 1.2, bgcolor: '#dcfce7', borderRadius: '6px', border: '1px solid #86efac' }}>
                                        <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, display: 'block' }}>
                                            CONTRÔLE D'APUREMENT STRICT DU COMPTE PIVOT :
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#14532d', display: 'block' }}>
                                            Crédit 411900 dans Journal FAC (100 000 000 CFA) = Débit 411900 dans Journal VTES (100 000 000 CFA) ➔ <strong>Solde Net du compte 411900 = 0 CFA (Entièrement soldé à chaque clôture)</strong>.
                                        </Typography>
                                    </Box>
                                </Paper>

                                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                    {/* TABLEAU 1 : JOURNAL DE FACTURATION (FAC) */}
                                    <Grid item xs={12} lg={6}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 1 }}>
                                            1. Journal de Facturation (FAC) — Constatation des Créances Clients
                                        </Typography>
                                        <TableContainer sx={{ border: '1px solid #0f172a', borderRadius: '8px' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: '#0f172a' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Compte</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Compte Tiers</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Libellé Écriture</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'right' }}>Débit (CFA)</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'right' }}>Crédit (CFA)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111AIMS</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - AIMS</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111ASPT</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - ASPT</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111DIVERS</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - CLIENTS DIVERS</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111FABRE</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - PIERRE FABRE</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111SONAG</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - SONAGED</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111AVOC</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - ORDRE DES AVOCATS</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#2563eb' }}>4111ELEC</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>FACT. FT... - ELECNOR</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>411100</TableCell>
                                                        <TableCell sx={{ color: 'text.secondary' }}>4111XXX / YYY / ZZZ</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>Autres créances clients en compte</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>20 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 800, color: '#0369a1' }}>411300</TableCell>
                                                        <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>VENTES CASH - mois/année (Comptant)</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 800, color: '#0369a1' }}>10 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover sx={{ bgcolor: '#fffbeb' }}>
                                                        <TableCell sx={{ fontWeight: 900, color: '#b45309' }}>411900</TableCell>
                                                        <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#b45309' }}>FACTURATION - mois/année (CLIENT FACTURATION)</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#b45309' }}>100 000 000</TableCell>
                                                    </TableRow>
                                                    <TableRow sx={{ bgcolor: '#f1f5f9', borderTop: '2px solid #0f172a' }}>
                                                        <TableCell colSpan={3} sx={{ fontWeight: 900, textAlign: 'right' }}>TOTAUX JOURNAL FAC :</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>100 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>100 000 000</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>

                                    {/* TABLEAU 2 : JOURNAL DES VENTES (VTES) */}
                                    <Grid item xs={12} lg={6}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 1 }}>
                                            2. Journal des Ventes (VTES) — Ventilation C.A., Débours & TVA
                                        </Typography>
                                        <TableContainer sx={{ border: '1px solid #0f172a', borderRadius: '8px' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: '#0f172a' }}>
                                                    <TableRow>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Compte</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Intitulé du Compte</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Libellé Écriture</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'right' }}>Débit (CFA)</TableCell>
                                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'right' }}>Crédit (CFA)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    <TableRow hover sx={{ bgcolor: '#fffbeb' }}>
                                                        <TableCell sx={{ fontWeight: 900, color: '#b45309' }}>411900</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 800 }}>CLIENT FACTURATION</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 700 }}>FACTURATION - mois/année (Apurement)</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#b45309' }}>100 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>471110</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>DEBOURS BILLETS D'AVION</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>DEBOURS BILLETS D'AVION - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>20 000 000</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>471120</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>DEBOURS CIRCUITS/EXCURSIONS</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>DEBOURS CIRCUITS/EXCURSIONS - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>20 000 000</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>471130</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>DEBOURS TRANSFERTS/AUTRES</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>DEBOURS TRANSFERTS/AUTRES - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>20 000 000</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>701100</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>VENTES BILLES D'AVION</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>VENTES BILLETTERIE - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>10 500 000</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>706110</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>VENTES CIRCUITS</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>VENTES CIRCUITS - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>5 000 000</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>706120</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>VENTES EXCURSIONS</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>VENTES EXCURSIONS - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>20 000 000</TableCell>
                                                    </TableRow>
                                                    <TableRow hover>
                                                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>443200</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>ETAT - TVA FACTUREE</TableCell>
                                                        <TableCell sx={{ fontSize: '0.8rem' }}>TVA FACTUREE - mois/année</TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>—</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>4 500 000</TableCell>
                                                    </TableRow>
                                                    <TableRow sx={{ bgcolor: '#f1f5f9', borderTop: '2px solid #0f172a' }}>
                                                        <TableCell colSpan={3} sx={{ fontWeight: 900, textAlign: 'right' }}>TOTAUX JOURNAL VTES :</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>100 000 000</TableCell>
                                                        <TableCell sx={{ textAlign: 'right', fontWeight: 900, color: '#166534' }}>100 000 000</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>

                                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px dashed #cbd5e1' }}>
                                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1.5 }}>
                                        <strong>Notes de gestion de l'articulation journalière :</strong>
                                        <br />• <strong>Compte 411300 ("CLIENT VENTES AU COMPTANT") :</strong> Enregistre les ventes au comptant sans création de compte tiers individuel.
                                        <br />• <strong>Compte 411900 ("CLIENT FACTURATION") :</strong> Compte pivot intermédiaire centralisateur permettant de dissocier la saisie continue des factures émises (Journal FAC) de la ventilation périodique du chiffre d'affaires marge, des débours et de la TVA (Journal VTES).
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* ═════════════════════════════════════════════════════════════ */}
                {/* ONGLET 6 : PARAMÈTRES D'EXPORT SAGE                           */}
                {/* ═════════════════════════════════════════════════════════════ */}
                {activeTab === 5 && (
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} flexWrap="wrap" gap={1.5}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', textTransform: 'uppercase' }}>
                                    Configuration du Fichier d'Interface SAGE
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Paramètres de formatage, d'encodage et de destination des fichiers d'export pour SAGE Comptabilité 100
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                size="small"
                                onClick={handleResetExportToDefaults}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Rétablir paramètres officiels
                            </Button>
                        </Stack>

                        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#ffffff', mb: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Nom du dossier / Entreprise SAGE"
                                        value={exportSettings.codeDossierSage || ''}
                                        onChange={(e) => setExportSettings({ ...exportSettings, codeDossierSage: e.target.value })}
                                        helperText="Identifiant officiel de la société (ex. AFRICA FOR TOURISM)"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Répertoire cible (dépôt ou bureau)"
                                        value={exportSettings.dossierDestination || ''}
                                        onChange={(e) => setExportSettings({ ...exportSettings, dossierDestination: e.target.value })}
                                        helperText="Emplacement local (ex. C:\Users\HP\Desktop)"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Préfixe du fichier généré"
                                        value={exportSettings.prefixeFichier || 'TM_BASCULE_'}
                                        onChange={(e) => setExportSettings({ ...exportSettings, prefixeFichier: e.target.value })}
                                        helperText="Exemple : TM_BASCULE_AAAAMMJJ.txt"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Séparateur de champs"
                                        value={exportSettings.separateur || ';'}
                                        onChange={(e) => setExportSettings({ ...exportSettings, separateur: e.target.value })}
                                        helperText="Séparateur pour l'import SAGE"
                                    >
                                        <MenuItem value=";">Point-virgule ( ; ) — Standard SAGE</MenuItem>
                                        <MenuItem value={'\t'}>Tabulation ( \t )</MenuItem>
                                        <MenuItem value=",">Virgule ( , )</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Format de fichier"
                                        value={exportSettings.formatFichier || 'TEXT_DELIMITED'}
                                        onChange={(e) => setExportSettings({ ...exportSettings, formatFichier: e.target.value })}
                                        helperText="Extension du fichier généré"
                                    >
                                        <MenuItem value="TEXT_DELIMITED">Texte délimité (.txt)</MenuItem>
                                        <MenuItem value="CSV">Fichier CSV (.csv)</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Encodage des caractères"
                                        value={exportSettings.encodage || 'UTF-8'}
                                        onChange={(e) => setExportSettings({ ...exportSettings, encodage: e.target.value })}
                                        helperText="Jeu de caractères pour l'import SAGE"
                                    >
                                        <MenuItem value="UTF-8">UTF-8 (Recommandé)</MenuItem>
                                        <MenuItem value="ANSI">ANSI (Windows-1252)</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSaveAll}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Enregistrer les paramètres d'export
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadSampleFile}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Tester téléchargement (.txt)
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadSampleCsvFile}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Tester téléchargement (.csv)
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px dashed #cbd5e1' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0f172a', mb: 1 }}>
                                Structure du Fichier d'Interface SAGE
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#475569', display: 'block', lineHeight: 1.6 }}>
                                Les écritures générées sont ordonnées selon les spécifications d'import SAGE 100 avec les champs :<br />
                                <strong>Date écriture</strong> (JJMMAA) ; <strong>Code Journal</strong> (FAC / VTES) ; <strong>N° Compte Général</strong> ; <strong>N° Compte Auxiliaire</strong> ; <strong>N° Pièce / Facture</strong> ; <strong>Libellé écriture</strong> ; <strong>Montant Débit</strong> ; <strong>Montant Crédit</strong> ; <strong>Date d'échéance</strong> ; <strong>Code lettrage</strong>
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* MODALE D'ÉDITION DU CODE TIERS CLIENT */}
            <Dialog open={clientTiersDialog.open} onClose={() => setClientTiersDialog({ open: false, client: null, codeTiers: '' })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ bgcolor: '#0f172a', color: 'white', py: 1.5 }}>
                    Affecter le compte auxiliaire SAGE
                </DialogTitle>
                <DialogContent sx={{ pt: 3, bgcolor: '#f8fafc' }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Client : <strong>{clientTiersDialog.client?.nomAffiche}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        label="Compte Auxiliaire SAGE"
                        value={clientTiersDialog.codeTiers}
                        onChange={(e) => setClientTiersDialog({ ...clientTiersDialog, codeTiers: e.target.value })}
                        helperText="Exemple : 4111FABRE, 4111BROGNI, etc."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setClientTiersDialog({ open: false, client: null, codeTiers: '' })}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveSingleClientTiers}>Enregistrer</Button>
                </DialogActions>
            </Dialog>

            {/* MODALE CRÉATION / ÉDITION COMPTE GÉNÉRAL */}
            <Dialog open={compteDialog.open} onClose={() => setCompteDialog({ ...compteDialog, open: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#0f172a', color: 'white', py: 1.5 }}>
                    {compteDialog.isEdit ? 'Modifier le compte général' : 'Création d\'un compte général'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3, bgcolor: '#f8fafc' }}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Numéro du compte"
                                value={compteDialog.data.numero || ''}
                                onChange={(e) => setCompteDialog({ ...compteDialog, data: { ...compteDialog.data, numero: e.target.value } })}
                                placeholder="ex. 411100, 706100"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Intitulé"
                                value={compteDialog.data.intitule || ''}
                                onChange={(e) => setCompteDialog({ ...compteDialog, data: { ...compteDialog.data, intitule: e.target.value } })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Nature de compte"
                                value={compteDialog.data.nature || 'Aucune'}
                                onChange={(e) => setCompteDialog({ ...compteDialog, data: { ...compteDialog.data, nature: e.target.value } })}
                            >
                                <MenuItem value="Aucune">Aucune</MenuItem>
                                <MenuItem value="Client">Client</MenuItem>
                                <MenuItem value="Trésorerie">Trésorerie</MenuItem>
                                <MenuItem value="Produits">Produits</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Sens normal"
                                value={compteDialog.data.sensNormal || 'Débit'}
                                onChange={(e) => setCompteDialog({ ...compteDialog, data: { ...compteDialog.data, sensNormal: e.target.value } })}
                            >
                                <MenuItem value="Débit">Débit</MenuItem>
                                <MenuItem value="Crédit">Crédit</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Code TVA applicable"
                                disabled={compteDialog.data.nature !== 'Produits'}
                                value={compteDialog.data.codeTva || ''}
                                onChange={(e) => setCompteDialog({ ...compteDialog, data: { ...compteDialog.data, codeTva: e.target.value } })}
                                helperText={compteDialog.data.nature === 'Produits' ? 'Obligatoire pour les comptes de Produits' : 'Inaccessible pour les autres natures de compte'}
                            >
                                <MenuItem value="">— Aucun code —</MenuItem>
                                {codesTva.map(t => <MenuItem key={t.id} value={t.code}>{t.code} — {t.intitule} ({t.taux}%)</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setCompteDialog({ ...compteDialog, open: false })}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveCompte}>Valider</Button>
                </DialogActions>
            </Dialog>

            {/* MODALE CRÉATION / ÉDITION JOURNAL */}
            <Dialog open={journalDialog.open} onClose={() => setJournalDialog({ ...journalDialog, open: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#0f172a', color: 'white', py: 1.5 }}>
                    {journalDialog.isEdit ? 'Modifier le journal' : 'Création d\'un code journal'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3, bgcolor: '#f8fafc' }}>
                    <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                        Le code et l'intitulé sont obligatoires.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Type de journal"
                                value={journalDialog.data.type || 'Général'}
                                onChange={(e) => setJournalDialog({ ...journalDialog, data: { ...journalDialog.data, type: e.target.value } })}
                            >
                                <MenuItem value="Général">Général (par défaut)</MenuItem>
                                <MenuItem value="Facturation">Facturation</MenuItem>
                                <MenuItem value="Ventes">Ventes</MenuItem>
                                <MenuItem value="Trésorerie">Trésorerie</MenuItem>
                                <MenuItem value="Achats">Achats</MenuItem>
                                <MenuItem value="Opérations diverses">Opérations diverses</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Code du journal"
                                required
                                value={journalDialog.data.code || ''}
                                onChange={(e) => setJournalDialog({ ...journalDialog, data: { ...journalDialog.data, code: e.target.value.toUpperCase() } })}
                                placeholder="ex. FAC, VTES, BQ"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Intitulé du journal"
                                required
                                value={journalDialog.data.intitule || ''}
                                onChange={(e) => setJournalDialog({ ...journalDialog, data: { ...journalDialog.data, intitule: e.target.value } })}
                                placeholder="ex. Journal de facturation, Journal des ventes"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Numérotation du journal"
                                value={journalDialog.data.numerotation || 'Continue'}
                                onChange={(e) => setJournalDialog({ ...journalDialog, data: { ...journalDialog.data, numerotation: e.target.value } })}
                            >
                                <MenuItem value="Continue">Continue (se poursuit de 1 à N)</MenuItem>
                                <MenuItem value="Mensuelle">Mensuelle (recommence à 1 au début de chaque mois)</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Type d'opérations couvertes (Description)"
                                value={journalDialog.data.description || ''}
                                onChange={(e) => setJournalDialog({ ...journalDialog, data: { ...journalDialog.data, description: e.target.value } })}
                                placeholder="ex. Facturation clients (Tourisme et Billetterie) — journal unique"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#475569' }}>
                                <strong>N.B. Règle de numérotation SAGE :</strong>
                                <br />• <strong>Continue :</strong> se poursuit sans interruption de 1 à N quel que soit le mois (ex. 1, 2, 3, …, 125…)
                                <br />• <strong>Mensuelle :</strong> recommence à 1 au début de chaque mois (ex. Janvier 1-85 / Février 1-63 / Mars 1-97)
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setJournalDialog({ ...journalDialog, open: false })}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveJournal}>Valider</Button>
                </DialogActions>
            </Dialog>

            {/* MODALE CRÉATION / ÉDITION CODE TVA */}
            <Dialog open={tvaDialog.open} onClose={() => setTvaDialog({ ...tvaDialog, open: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#0f172a', color: 'white', py: 1.5 }}>
                    {tvaDialog.isEdit ? 'Modifier le code TVA' : 'Création de taux et taxes'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3, bgcolor: '#f8fafc' }}>
                    <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                        Le code taxe et l'intitulé sont obligatoires.
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Code taxe"
                                required
                                value={tvaDialog.data.code || ''}
                                onChange={(e) => setTvaDialog({ ...tvaDialog, data: { ...tvaDialog.data, code: e.target.value.toUpperCase() } })}
                                placeholder="ex. TVA1, TVA0, TVAEX"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Sens"
                                value={tvaDialog.data.sens || 'Facturée'}
                                onChange={(e) => setTvaDialog({ ...tvaDialog, data: { ...tvaDialog.data, sens: e.target.value } })}
                            >
                                <MenuItem value="Facturée">Facturée (ventes)</MenuItem>
                                <MenuItem value="Déductible">Déductible (achats)</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Intitulé taxe"
                                required
                                value={tvaDialog.data.intitule || ''}
                                onChange={(e) => setTvaDialog({ ...tvaDialog, data: { ...tvaDialog.data, intitule: e.target.value } })}
                                placeholder="ex. TVA taux normal 18%, Exonéré billetterie 0%"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                type="number"
                                fullWidth
                                size="small"
                                label="Taux (%)"
                                value={tvaDialog.data.taux ?? 18}
                                onChange={(e) => setTvaDialog({ ...tvaDialog, data: { ...tvaDialog.data, taux: Number(e.target.value) } })}
                                placeholder="18"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Compte de taxe SAGE"
                                value={tvaDialog.data.compteTaxe || ''}
                                onChange={(e) => setTvaDialog({ ...tvaDialog, data: { ...tvaDialog.data, compteTaxe: e.target.value } })}
                                placeholder="ex. 443200"
                                helperText="ex. 443200 (laisser vide si exonéré)"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Application / Description"
                                value={tvaDialog.data.description || ''}
                                onChange={(e) => setTvaDialog({ ...tvaDialog, data: { ...tvaDialog.data, description: e.target.value } })}
                                placeholder="ex. TVA taux normal — prestations touristiques soumises"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#475569' }}>
                                <strong>N.B. — Lien entre comptes de produits et codes TVA :</strong>
                                <br />• <strong>Comptes taxables :</strong> liés à TVA1 (18%) avec cumul dans le compte <strong>443200</strong> (ETAT - TVA FACTUREE).
                                <br />• <strong>Comptes exonérés :</strong> liés à TVA0 (0%) sans compte de taxe mouvementé.
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setTvaDialog({ ...tvaDialog, open: false })}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveTva}>Valider</Button>
                </DialogActions>
            </Dialog>

            {/* TOAST SNACKBAR */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ParametrageComptablePage;
